import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { QueryState } from '@/shared/ui/QueryState'
import { OrgDetailMobile } from '@/public/features/org/ui/OrgDetailMobile'
import { OrgDetailPc } from '@/public/features/org/ui/OrgDetailPc'
import { api } from '@/shared/lib/api'
import { CERT_LABEL, makeLabeler, makeRegionLabeler, useCommonCodes, useRegions } from '@/shared/lib/codes'

export function OrgDetailPage() {
  const { id } = useParams<{ id: string }>()
  const org = useQuery({
    queryKey: ['org', id],
    queryFn: () => api.org(id ?? ''),
    enabled: Boolean(id),
  })

  const orgTypeLabel = makeLabeler(useCommonCodes('org_type').data)
  const categoryLabel = makeLabeler(useCommonCodes('category').data)
  const regionLabel = makeRegionLabeler(useRegions().data)

  const o = org.data

  return (
    <>
      <OrgDetailMobile query={org} />
      <OrgDetailPc query={org} />

      <div className="desktop-screen">
      <Link className="mb-4 inline-block text-label text-black-100" to="/solutions">
        ← 기업해결카드 목록
      </Link>
      <QueryState isPending={org.isPending} error={org.error}>
        {o ? (
          <>
            <article className="rounded-[10px] border border-white-300 bg-white-50 p-7">
              <div className="mb-5 border-b border-white-300 pb-5">
                <h1 className="m-0 mb-1.5 text-headline-sm font-bold tracking-[-0.02em]">
                  {o.name}{' '}
                  {o.certType && CERT_LABEL[o.certType] ? (
                    <span className={o.certType === 'cert_certified' ? 'inline-block rounded-full border border-white-400 px-2 py-0.5 text-[11px] font-semibold border-black-600 bg-black-600 text-white-50' : 'inline-block rounded-full border border-white-400 px-2 py-0.5 text-[11px] font-semibold bg-white-200 text-black-500'}>
                      {CERT_LABEL[o.certType]}
                    </span>
                  ) : null}
                </h1>
                <p className="text-black-100" style={{ margin: 0 }}>
                  {orgTypeLabel(o.orgType)}
                </p>
              </div>

              {o.intro ? <p style={{ whiteSpace: 'pre-wrap' }}>{o.intro}</p> : null}

              {o.mission ? (
                <p style={{ whiteSpace: 'pre-wrap' }}>
                  <b>미션</b> — {o.mission}
                </p>
              ) : null}
              {o.vision ? (
                <p style={{ whiteSpace: 'pre-wrap' }}>
                  <b>비전</b> — {o.vision}
                </p>
              ) : null}

              <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2.5 text-body-sm">
                <dt>소재지</dt>
                <dd>
                  {o.baseRegionCode ? regionLabel(o.baseRegionCode) : <span className="text-black-100">확인 필요</span>}
                </dd>

                <dt>설립</dt>
                <dd>{o.foundedYear !== null ? `${o.foundedYear}년` : <span className="text-black-100">확인 필요</span>}</dd>

                <dt>구성원</dt>
                <dd>{o.memberScale ?? <span className="text-black-100">확인 필요</span>}</dd>

                <dt>홈페이지</dt>
                <dd>
                  {o.homepageUrl ? (
                    <a href={o.homepageUrl} target="_blank" rel="noreferrer noopener">
                      {o.homepageUrl}
                    </a>
                  ) : (
                    <span className="text-black-100">확인 필요</span>
                  )}
                </dd>
              </dl>
            </article>

            <h2 className="mt-10 mb-3.5 text-body-md font-bold">이 기업의 해결카드 {o.solutionCards.length}개</h2>
            {o.solutionCards.length === 0 ? (
              <p className="text-black-100 text-label">아직 공개된 해결카드가 없습니다.</p>
            ) : (
              <div className="grid grid-cols-1 gap-3.5 min-[601px]:grid-cols-2 min-[901px]:grid-cols-3">
                {o.solutionCards.map((c) => (
                  <Link key={c.id} className="block rounded-[10px] border border-white-300 bg-white-50 p-[18px] transition-[border-color] duration-150 ease-linear hover:border-white-400" to={`/solutions/${c.id}`}>
                    <h3>{c.title}</h3>
                    <p>{c.summary}</p>
                    <div className="flex flex-wrap gap-[5px]">
                      {c.categoryCodes.map((code) => (
                        <span key={code} className="rounded-full bg-white-200 px-[9px] py-[3px] text-[11px] text-black-500">
                          {categoryLabel(code)}
                        </span>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        ) : null}
      </QueryState>
      </div>
    </>
  )
}
